import { useEffect, useRef, useState, type FormEvent } from "react";
import { ApiError } from "../api/client";
import { resendPhoneOtp, verifyPhone } from "../api/auth";

/**
 * Phone verification, shown after sign-in is refused because the number has not
 * been confirmed.
 *
 * NON-COLLAPSIBLE BY DESIGN. Signing in is not complete until this is done —
 * the API refuses an unverified number — so there is nothing to go "back" to.
 * A dismissible dialog here would be a trap: the user closes it, lands on the
 * sign-in form, types the same password, and gets the same refusal, with no
 * route forward. Escape does nothing, clicking the backdrop does nothing, and
 * the only way out is a code or a "use a different account" escape hatch that
 * is always visible rather than hidden.
 *
 * The account identifier is held by the caller and never re-prompted for: the
 * user already typed it, and asking again invites the typo that produced the
 * whole problem.
 */
export function PhoneVerifyModal({
  identifier,
  onVerified,
  onSignOut,
}: {
  /** The phone number the code was sent to. */
  identifier: string;
  /** Called after a successful verification, so the caller can retry sign-in. */
  onVerified: () => void;
  /** The only escape: leave this account and sign in as someone else. */
  onSignOut: () => void;
}) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resent, setResent] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Request a code as soon as this opens, so the copy underneath is always
  // true. Sign-in only discovers the number is unconfirmed at this point — no
  // code was issued moments earlier — so without this the panel would tell the
  // user to enter a code that may be days old or never existed.
  //
  // The send deliberately is NOT on the login request. Doing it server-side
  // would mean every blocked sign-in attempt costs an SMS, which is a cheap way
  // to burn the account balance; here it costs one message per modal a person
  // actually opened.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await resendPhoneOtp(identifier);
        if (!cancelled) setResent(true);
      } catch {
        // The code field stays usable and "send again" remains, so a failed
        // auto-send is not a dead end.
        if (!cancelled) setError("We could not send a code automatically. Use the button below.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [identifier]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (code.trim().length < 4) {
      setError("Enter the six-digit code from the text message.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await verifyPhone(identifier, code.trim());
      onVerified();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "That code did not work.");
      setCode("");
      inputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  async function onResend() {
    setBusy(true);
    setError(null);
    setResent(false);
    try {
      await resendPhoneOtp(identifier);
      setResent(true);
      setTimeout(() => setResent(false), 6000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not send another code.");
    } finally {
      setBusy(false);
    }
  }

  return (
    // role=alertdialog + aria-modal tells assistive tech this owns the screen.
    // There is deliberately no dismiss control.
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="phone-verify-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/45 p-4"
    >
      <div className="w-full max-w-sm border-t-[3px] border-primary bg-raised p-6 shadow-lg">
        <h2 id="phone-verify-title" className="text-xl font-semibold tracking-tight">
          Confirm your phone
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-ink-muted">
          We sent a six-digit code to <span className="tnum">{identifier}</span>. Enter it to finish
          setting up this account.
        </p>

        <form onSubmit={onSubmit} className="mt-5">
          <label className="label" htmlFor="verify-code">
            Verification code
          </label>
          <input
            id="verify-code"
            ref={inputRef}
            name="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? "verify-code-error" : undefined}
            className="field field-mono tnum text-center text-[22px] tracking-[0.3em]"
          />

          {error ? (
            <p
              id="verify-code-error"
              role="alert"
              className="mt-3 rounded-r border-l-[3px] border-danger bg-danger-wash px-3.5 py-2.5 text-sm text-danger"
            >
              {error}
            </p>
          ) : null}

          {resent ? (
            <p className="mt-3 text-sm text-primary">Code sent. It can take a few seconds to arrive.</p>
          ) : null}

          <button
            type="submit"
            disabled={busy || code.trim().length < 4}
            className="mt-5 min-h-11 w-full rounded bg-primary px-4 py-2.5 text-sm font-medium text-white disabled:opacity-45"
          >
            {busy ? "Checking…" : "Confirm and sign in"}
          </button>
        </form>

        <button
          type="button"
          onClick={onResend}
          disabled={busy}
          className="mt-3 min-h-11 w-full rounded px-3 py-2 text-sm font-medium text-primary underline disabled:opacity-50"
        >
          Send the code again
        </button>

        {/*
          The exit. Sign-in cannot be completed without this step, so it stays
          visible rather than hiding behind a close icon that would dead-end.
        */}
        <p className="mt-4 border-t border-line pt-4 text-sm text-ink-muted">
          Wrong number?{" "}
          <button
            type="button"
            onClick={onSignOut}
            disabled={busy}
            className="font-medium text-ink underline disabled:opacity-50"
          >
            Use a different account
          </button>
        </p>
      </div>
    </div>
  );
}